import {
  Domaene,
  Einbruchmeldeanlage,
  EinsatzgebieteTier,
  GegeDruckdaten,
  GegenstandZusammenfassungDatenDruck,
  HoeherwertigerSelection,
  JhpDruckdaten,
  Keller,
  MechanischeSicherung,
  Modeaccessoires as Maccessoires,
  NichtVerschlusspflichtigeWertsachen as NVPWertsachen,
  PhDruckdaten,
  RsDruckdaten,
  RsVermietungDruckdaten,
  SicherungsglaeubigerDruck,
  SparteDruckdaten,
  SpartenType,
  TerrasseBalkon,
  ThhHundDruckdaten,
  ThhPferdDruckdaten,
  Tiergeschlecht,
  TkvDiagnoseDruck,
  TkvDruckdaten,
  TkvV2Druckdaten,
  Tresor,
  TresorSicherheitsstufe,
  Tuer,
  UnderwriterDecisionDruck,
  UriDruckdaten,
  Verbauung,
  VermieteteWohnungDruck,
  VerschlusspflichtigeWertsachen as VPWertsachen,
  VhvDruckdaten,
  VwgDruckdaten
} from '@ps20/bff-models';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import dayjs from 'dayjs';
import {
  App,
  areAppTypesEquivalent,
  getSpartenTypeByApp,
  isHausratSparte,
  numberToGermanNumberFormattedString
} from '@ps20-frontend/frontend-models';
import {
  buildUwWireDecision,
  findUwVereinbarungText,
  findUwZuschlagProzent,
  getAllUwContexts,
  selectUwContextsForSparte,
  UwPrintField,
  selectUwEntries,
  UwContextEntry,
  UwSharedDecision,
} from '@ps20-frontend/uw';
import { convertDate, DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO, isNullOrUndefined } from '@ps20-frontend/utils';
import { TkvV2HealthStatus } from '@ps20/bff-models/dist/lib/ps20-bff-model-oaas';
import { IAppState } from '../../localStore';
import {
  GegeCheckoutProdukt, Modeaccessoires,
  NichtVerschlusspflichtigeWertsachen,
  Produkt,
  Sicherungsglaeubiger,
  SicherungVorhanden,
  SicherungVorhandenMitRelevanz,
  TresorEintrag,
  TresorVerbauung,
  VerschlusspflichtigeWertsachen,
  VhvCheckoutProdukt,
  VwgCheckoutProdukt
} from '../../model';
import { HealthStatusEntry } from '../../model/healthStatus';
import { VermieteteWohnung } from '../../reducer/vermietete-wohnungen.reducer';
import { selectAmtlichesKennzeichen, selectWartezeitverzicht } from '../../selector/produkt.selector';
import { ThhHundGeschlecht } from '../../reducer/thhHundAngaben.reducer';
import { ThhPferdGeschlecht } from '../../reducer/thhPferdAngaben.reducer';
import { selectSicherungsglaeubigerForAngebot } from '../../selector/sicherungsglaeubiger.selector';
import { DiagnoseAngaben, TkvAngabenElement } from '../../reducer/tkvAngaben.reducer';
import { VpAngaben } from '../../reducer/unfAngaben.reducer';
import { INITIAL_EMA, INITIAL_MECHANISCHE_SICHERUNGEN, INITIAL_TRESOR_EINTRAG } from '../../reducer/produkte.reducer';
import { HoeherwertigerHausratUtils } from '../../../utils/hoeherwertiger-hausrat.utils';
import { TkvAngabenV2Item } from '../../reducer/tkvAngabenV2.reducer';
import { hasGefahrenInVorversicherung } from '../../selector/vorversicherung.selector';

const hoeherwertigerHausratUtils = new HoeherwertigerHausratUtils();

export function mapCheckoutInto(
  state: IAppState,
  sparteDruckdaten: SparteDruckdaten
): SparteDruckdaten {
  const result = cloneDeep(sparteDruckdaten);

  if (isVwg(sparteDruckdaten)) {
    const siglForAngebot = selectSicherungsglaeubigerForAngebot(
      state.sicherungsglaeubiger,
      sparteDruckdaten.angebotsId
    );
    (result as VwgDruckdaten).sicherungsglaeubigerDruck = mapSigls(siglForAngebot);
    if (sparteDruckdaten.spartenType === SpartenType.WOHNGEBAEUDE_V2) {
      const vwgCheckoutProdukt = state.produkte?.find(
        produkt =>
          getSpartenTypeByApp(produkt.appId) === sparteDruckdaten.spartenType &&
          produkt.angebotsId === sparteDruckdaten.angebotsId
      ) as VwgCheckoutProdukt;
      result.hasGefahrenInVorversicherung = hasGefahrenInVorversicherung(
        vwgCheckoutProdukt,
        state.vorversicherungen
      );
    }
    applyUnderwriterDruckdaten(state, sparteDruckdaten, result);
  } else if (isPh(sparteDruckdaten)) {
    (result as PhDruckdaten).vermieteteWohnungDruckList = mapVermieteteWohnungen(state.vermieteteWohnungen);
  } else if (isRsVerkehr(sparteDruckdaten)) {
    (result as RsDruckdaten).fahrzeugDruck.amtlichesKennzeichen = selectAmtlichesKennzeichen(state);
  } else if (isRsKombi(sparteDruckdaten)) {
    const wartezeitverzicht = selectWartezeitverzicht(App.RS)(state);
    (result as RsDruckdaten).wartezeitverzichtDruck = {
      selected: wartezeitverzicht.wartezeitverzichtSelected,
      referenzvertrag: wartezeitverzicht.referenzvertrag
    };
  } else if (isThhHund(result)) {
    applyThhHundDruckdaten(result as ThhHundDruckdaten, state);
  } else if (isThhPferd(result)) {
    applyThhPferdDruckdaten(result as ThhPferdDruckdaten, state);
  } else if (isTkv(result)) {
    applyTkvDruckdaten(result, state, sparteDruckdaten.angebotsId);
  } else if (isJhp(result)) {
    applyJhpDruckdaten(result as JhpDruckdaten, state);
  } else if (isUri(result)) {
    applyUriDruckdaten(result as UriDruckdaten, state);
  } else if (isGege(result)) {
    applyGegeDruckdaten(result as GegeDruckdaten, state);
  } else if (isVhv(result)) {
    applyVhvDruckdaten(result as VhvDruckdaten, state);
    applyUnderwriterDruckdaten(state, sparteDruckdaten, result);
  } else if (isRsVermietung(result)) {
    const druckdaten = result as RsVermietungDruckdaten;
    const wartezeitverzicht = selectWartezeitverzicht(App.RS_VERMIETUNG)(state);
    druckdaten.wartezeitverzichtDruck = {
      selected: wartezeitverzicht.wartezeitverzichtSelected,
      referenzvertrag: wartezeitverzicht.referenzvertrag
    };
  } else if (isTkvV2(result)) {
    applyTkvV2Druckdaten(result, state, sparteDruckdaten.angebotsId);
  }

  return result;
}

function isVwg(arg: SparteDruckdaten): boolean {
  return arg.spartenType === SpartenType.WOHNGEBAEUDE || arg.spartenType === SpartenType.WOHNGEBAEUDE_V2;
}

function isPh(arg: SparteDruckdaten): boolean {
  return arg.spartenType === SpartenType.PRIVATHAFTPFLICHT;
}

function isRsVerkehr(arg: SparteDruckdaten): boolean {
  return arg.spartenType === SpartenType.RECHTSSCHUTZ && (arg as RsDruckdaten).isKombi === false;
}

function isRsKombi(arg: SparteDruckdaten): boolean {
  return arg.spartenType === SpartenType.RECHTSSCHUTZ && (arg as RsDruckdaten).isKombi === true;
}

export function isThhHund(sparteDruckdaten: SparteDruckdaten): boolean {
  return sparteDruckdaten.spartenType === SpartenType.TIERHALTERHAFTPFLICHT_HUND;
}

function isThhPferd(sparteDruckdaten: SparteDruckdaten): boolean {
  return sparteDruckdaten.spartenType === SpartenType.TIERHALTERHAFTPFLICHT_PFERD;
}

function isTkv(sparteDruckdaten: SparteDruckdaten): sparteDruckdaten is TkvDruckdaten {
  return sparteDruckdaten.spartenType === SpartenType.TIERKRANKEN;
}

function isTkvV2(sparteDruckdaten: SparteDruckdaten): sparteDruckdaten is TkvV2Druckdaten {
  return sparteDruckdaten.spartenType === SpartenType.TIERKRANKEN_V2;
}

function mapSigls(sigls: Array<Sicherungsglaeubiger>): SicherungsglaeubigerDruck[] {
  return sigls.map(sicherungsglaeubiger => {
    return {
      adresse: {
        plz: sicherungsglaeubiger.plz,
        ort: sicherungsglaeubiger.ort,
        strasse: sicherungsglaeubiger.strasse,
        hausnummer: sicherungsglaeubiger.hausnr,
        postfach: sicherungsglaeubiger.postfach
      },
      aktenzeichen: sicherungsglaeubiger.aktenzeichen,
      bankname: sicherungsglaeubiger.name
    };
  });
}

type UnderwriterPrintableDruckdaten = SparteDruckdaten & {
  [field in UwPrintField]?: UnderwriterDecisionDruck;
};

function applyUnderwriterDruckdaten(state: IAppState, sparteDruckdaten: SparteDruckdaten, result: SparteDruckdaten): void {
  const checkoutProdukt = state.produkte?.find(
    produkt =>
      getSpartenTypeByApp(produkt.appId) === sparteDruckdaten.spartenType &&
      produkt.angebotsId === sparteDruckdaten.angebotsId
  ) as VwgCheckoutProdukt | VhvCheckoutProdukt;

  const shared = checkoutProdukt?.uw?.shared;
  const descriptors = selectUwContextsForSparte(getAllUwContexts(), sparteDruckdaten.spartenType);

  descriptors.forEach(descriptor => {
    if (!descriptor.wire) {
      return;
    }
    const entry = selectUwEntries(checkoutProdukt?.uw, descriptor.key)[0];
    applyUnderwriterDecisionDruck(entry, shared, descriptor.wire.printField, result as UnderwriterPrintableDruckdaten);
  });
}

function applyUnderwriterDecisionDruck(
  entry: UwContextEntry | undefined,
  shared: UwSharedDecision | undefined,
  printField: UwPrintField,
  result: UnderwriterPrintableDruckdaten
): void {
  const decision = buildUwWireDecision(entry, shared);
  if (!decision || !shared) {
    return;
  }

  result[printField] = decision;

  if (shared.measures.length === 0) {
    return;
  }

  // `|| null` drops a 0 % Zuschlag from print; the submission mapper deliberately keeps it with `?? null`
  result[printField].massnahmen = {
    zuschlag: findUwZuschlagProzent(shared) || null,
    vereinbarung: findUwVereinbarungText(shared) || null,
  };
}

function mapVermieteteWohnungen(
  vermieteteWohnungen: Array<VermieteteWohnung>
): VermieteteWohnungDruck[] {
  return vermieteteWohnungen.map(vermieteteWohnung => {
    return {
      adresse: {
        plz: vermieteteWohnung.plz,
        ort: vermieteteWohnung.ort,
        strasse: vermieteteWohnung.strasse,
        hausnummer: vermieteteWohnung.hausnummer,
        postfach: null
      },
      wohnungsnummer: vermieteteWohnung.wohnungsnummer
    } as VermieteteWohnungDruck;
  });
}

function applyThhHundDruckdaten(thhHundDruckdaten: ThhHundDruckdaten, state: IAppState): void {
  const thhHundDomaenenwertSchluesselAusStrecke = thhHundDruckdaten.versicherteTiere.map(
    versicherterHund => versicherterHund.rasse
  );
  thhHundDruckdaten.versicherteTiere = state.thhHundAngaben.angabenListe.map((hund, index) => ({
    geburtsdatum: convertToThhGeburtsdatum(hund.geburtsdatum),
    chipnummer: hund.chipnummer,
    geschlecht:
      hund.geschlecht === ThhHundGeschlecht.M ? Tiergeschlecht.MAENNLICH : Tiergeschlecht.WEIBLICH,
    taetonummer: hund.taetowierungsnummer,
    tiername: hund.name,
    rasse: thhHundDomaenenwertSchluesselAusStrecke[index]
  }));
  const meldebehoederState = state.meldebehoerde;
  thhHundDruckdaten.meldebehoerde = meldebehoederState.isMeldebehoerdeVorhanden
    ? {
      name: meldebehoederState.behoerdename,
      aktenzeichen: meldebehoederState.isAktenzeichenUnbekannt
        ? null
        : meldebehoederState.aktenzeichen,
      hausnummer: meldebehoederState.hausnummer,
      ort: meldebehoederState.ort,
      plz: meldebehoederState.plz,
      strasse: meldebehoederState.strasse
    }
    : null;
}

function applyThhPferdDruckdaten(thhPferdDruckdaten: ThhPferdDruckdaten, state: IAppState): void {
  const thhPferdegroesseAusStrecke = thhPferdDruckdaten.versicherteTiere.map(
    versichertesPferd => versichertesPferd.tiergroesse
  );
  thhPferdDruckdaten.versicherteTiere = state.thhPferdAngaben.angabenListe.map((pferd, index) => ({
    chipnummer: pferd.chipnummer,
    geburtsdatum: convertToThhGeburtsdatum(pferd.geburtsdatum),
    lebensnummer: pferd.lebensnummer,
    geschlecht:
      pferd.geschlecht === ThhPferdGeschlecht.M
        ? Tiergeschlecht.MAENNLICH
        : Tiergeschlecht.WEIBLICH,
    tiername: pferd.name,
    tiergroesse: thhPferdegroesseAusStrecke[index],
    rasse: pferd.rasse
  }));
}

function applyTkvDruckdaten(
  tkvDruckdaten: TkvDruckdaten,
  state: IAppState,
  angebotsId: string
): void {
  const tkvAngaben: TkvAngabenElement = state.tkvAngaben.angabenListe.find(
    elem => elem.angebotsId === angebotsId
  );
  tkvDruckdaten.versichertesTier.tiername = tkvAngaben.name;
  tkvDruckdaten.versichertesTier.istBeiTierarzt = mapIsBeiTierarzt(
    tkvAngaben.istBeiTierarzt,
    tkvDruckdaten
  );
  tkvDruckdaten.versichertesTier.einsatzgebiet = tkvAngaben.einsatzgebiet;
  tkvDruckdaten.versichertesTier.kennzeichnung = tkvAngaben.kennzeichnung;
  tkvDruckdaten.versichertesTier.kennzeichnungsArt = tkvAngaben.kennzeichnungsArt;
  tkvDruckdaten.versichertesTier.tkvDiagnoseDruckList = mapToBffTkvDiagnoseDruck(
    tkvAngaben.diagnoseAngaben,
    tkvDruckdaten,
    state
  );
}

function mapIsBeiTierarzt(istBeiTierarzt: boolean, tkvDruckdaten: TkvDruckdaten): boolean {
  const diagnosenAusVorvertrag =
    tkvDruckdaten.versichertesTier?.tkvDiagnoseDruckList &&
    tkvDruckdaten.versichertesTier?.tkvDiagnoseDruckList.length > 0;
  return diagnosenAusVorvertrag ? true : istBeiTierarzt;
}

function mapToBffTkvDiagnoseDruck(
  diagnoseAngaben: DiagnoseAngaben[],
  tkvDruckdaten: TkvDruckdaten,
  state: IAppState
): TkvDiagnoseDruck[] {
  const diagnosenDetailListe: Domaene[] = state.tkvAngaben?.diagnosenDetail;
  const tkvDiagnoseDruckList: TkvDiagnoseDruck[] =
    tkvDruckdaten.versichertesTier?.tkvDiagnoseDruckList;
  if (tkvDiagnoseDruckList && tkvDiagnoseDruckList.length > 0) {
    return tkvDruckdaten?.versichertesTier.tkvDiagnoseDruckList.map(angabe => {
      if (diagnosenDetailListe) {
        const detailWert = diagnosenDetailListe.find(
          detail => detail.text === angabe.gewaehlteDiagnoseDetail
        )?.wert;
        return {
          gewaehlteDiagnose: angabe.gewaehlteDiagnose,
          gewaehlteDiagnoseDetail: detailWert || null,
          diagnoseBeschreibung: angabe.diagnoseBeschreibung
        };
      }
      return {
        gewaehlteDiagnose: angabe.gewaehlteDiagnose,
        gewaehlteDiagnoseDetail: null,
        diagnoseBeschreibung: 'in maintenance'
      };
    });
  }
  return diagnoseAngaben.map(angabe => ({
    gewaehlteDiagnose: angabe?.gewaehlteDiagnose?.wert,
    gewaehlteDiagnoseDetail: angabe?.gewaehlteDiagnoseDetail?.wert,
    diagnoseBeschreibung: angabe?.diagnoseBeschreibung
  }));
}

function convertToThhGeburtsdatum(geburtsdatum: string): string {
  return geburtsdatum && dayjs(geburtsdatum, DATEFORMAT_LOCAL_STATE).format(DATEFORMAT_SHORT_ISO);
}

const isJhp = (sparteDruckDaten: SparteDruckdaten): boolean =>
  sparteDruckDaten.spartenType == SpartenType.JAGDHAFTPFLICHT;

const applyJhpDruckdaten = (druckDaten: JhpDruckdaten, state: IAppState): void => {
  druckDaten.jagdbehoerde = {
    plz: state.jhpAngaben.jagdBehoerde.plz,
    ort: state.jhpAngaben.jagdBehoerde.ort,
    strasse: state.jhpAngaben.jagdBehoerde.strasse,
    hausnummer: state.jhpAngaben.jagdBehoerde.hausnummer,
    name: state.jhpAngaben.jagdBehoerde.behoerdename
  };
};
const isGege = (sparteDruckDaten: SparteDruckdaten): boolean =>
  sparteDruckDaten.spartenType == SpartenType.GEGENSTANDSSCHUTZ;

function createGegenstaendeZusammenfassung(
  produktForDruckdaten: GegeCheckoutProdukt
): GegenstandZusammenfassungDatenDruck[] {
  const gegenstaende = produktForDruckdaten.gegenstaende;
  const zusammenfassung = new Map<string, GegenstandZusammenfassungDatenDruck>();

  gegenstaende.forEach(gegenstand => {
    if (zusammenfassung.has(gegenstand.kategorie.name)) {
      const kategorieZusammenfassung = zusammenfassung.get(gegenstand.kategorie.name);
      zusammenfassung.set(gegenstand.kategorie.name, {
        kategorie: kategorieZusammenfassung.kategorie,
        anzahl: kategorieZusammenfassung.anzahl + 1,
        summe: kategorieZusammenfassung.summe + gegenstand.versicherungssumme
      });
    } else {
      zusammenfassung.set(gegenstand.kategorie.name, {
        kategorie: gegenstand.kategorie.name,
        anzahl: 1,
        summe: gegenstand.versicherungssumme
      });
    }
  });

  return Array.from(zusammenfassung.values());
}

const applyGegeDruckdaten = (druckDaten: GegeDruckdaten, state: IAppState): void => {
  const produktForDruckdaten: GegeCheckoutProdukt = state.produkte.find(
    produkt =>
      getSpartenTypeByApp(produkt.appId) === SpartenType.GEGENSTANDSSCHUTZ &&
      produkt.angebotsId === druckDaten.angebotsId
  );
  druckDaten.gegenstaende = produktForDruckdaten.gegenstaende.map(gegenstand => ({
    kategorie: gegenstand.kategorie.name,
    marke: gegenstand.modell,
    kaufpreis: gegenstand.versicherungssumme,
    kaufdatum: convertDate(gegenstand.kaufdatum),
    zusatzinfo: gegenstand.informationen,
    beitrag: gegenstand.beitrag
  }));
  druckDaten.gegenstandZusammenfassung = createGegenstaendeZusammenfassung(produktForDruckdaten);
};

const applyUriDruckdaten = (druckDaten: UriDruckdaten, state: IAppState): void => {
  const produktForDruckdaten: Produkt = state.produkte.find(
    produkt =>
      getSpartenTypeByApp(produkt.appId) === SpartenType.UNFALL &&
      produkt.angebotsId === druckDaten.angebotsId
  );
  const vpAngabe: VpAngaben = state.unfAngaben.vpAngabenListe.find(
    angaben => angaben.angebotsId === produktForDruckdaten.angebotsId
  );
  if (vpAngabe) {
    druckDaten.hasDynamik = vpAngabe.dynamikSelected;
    druckDaten.bezugsrecht = vpAngabe.bezugsrecht;
    druckDaten.bezugsrechtPersonen = [];
    druckDaten.isSofortSchutzBesprochen = vpAngabe.isSofortschutzBesprochen;
    vpAngabe.bezugsberechtigten?.forEach(bezugsberechtigter => {
      druckDaten.bezugsrechtPersonen.push({
        adresse: bezugsberechtigter.adresse,
        nachname: bezugsberechtigter.nachname,
        vorname: bezugsberechtigter.vorname,
        anrede: bezugsberechtigter.anrede,
        personenID: bezugsberechtigter.kdvId,
        geburtsdatum: convertDate(bezugsberechtigter.geburtsdatum),
        adelsbez: bezugsberechtigter.adelsbez?.text,
        adelstitel: bezugsberechtigter.adelstitel?.text,
        akadgrad: bezugsberechtigter.akadgrad?.text,
        anteilBezugsrecht: numberToGermanNumberFormattedString(bezugsberechtigter.anteil),
        firmenname: null,
        businessForm: null,
        juristisch: false
      });
    });
    druckDaten.pflegebeduerftigt = vpAngabe.pflegeFrageAntwort;
    druckDaten.beeintraechtigungen = vpAngabe.beeintraechtigungen;
  }
  druckDaten.famRolle = state.beratung.assekuranzPersonenAngaben.find(
    angaben => angaben.angebotsId === produktForDruckdaten.angebotsId
  )?.beziehungZumVn;
};

const isUri = (sparteDruckDaten: SparteDruckdaten): boolean =>
  sparteDruckDaten.spartenType == SpartenType.UNFALL;

const isVhv = (result: SparteDruckdaten): boolean => isHausratSparte(result.spartenType);

const isRsVermietung = (result: SparteDruckdaten): boolean =>
  result.spartenType === SpartenType.RECHTSSCHUTZ_VERMIETUNG;

const applyVhvDruckdaten = (result: VhvDruckdaten, state: IAppState): void => {
  const vhvProdukt = state.produkte.find(
    produkt =>
      result.angebotsId === produkt.angebotsId && areAppTypesEquivalent(produkt.appId, App.VHV)
  ) as VhvCheckoutProdukt;

  if (vhvProdukt) {
    if (vhvProdukt.appId === App.VHV_V2) {
      result.hasGefahrenInVorversicherung = hasGefahrenInVorversicherung(
        vhvProdukt,
        state.vorversicherungen
      );
    }

    const hoeherwertigerHausratActive =
      shouldMapWertsachen(vhvProdukt) ||
      hoeherwertigerHausratUtils.showMechansicheSicherungKachel(vhvProdukt) ||
      shouldActivateForWertsachenfrageDenied(vhvProdukt);

    if (hoeherwertigerHausratActive) {
      const hasWertsachen = mapHasWertsachen(vhvProdukt);
      const shouldMapWertsachenValues =
        (!vhvProdukt.staendigBewohnt && vhvProdukt.versicherungssumme > 50_000) ||
        !hoeherwertigerHausratUtils.checkIfShouldShowWersachensummeQuestion(vhvProdukt) ||
        vhvProdukt.hasWertsachen || vhvProdukt.isTragerisikoSelected;
      result.hoeherwertigenWertsachen = shouldMapWertsachenValues
        ? {
            hasWertsachen: hasWertsachen,
            modeaccessoires: vhvProdukt.appId === App.VHV_V2 ? mapModeaccessoires(vhvProdukt) : undefined,
            verschlusspflichtigeWertsachen: mapVerschlusspflichtigenWertsachen(vhvProdukt),
            nichtVerschlusspflichtigeWertsachen:
              mapNichtVerschlusspflichtigenWertsachen(vhvProdukt),
            gesamtsummeWertsachen: mapGesamtsummeWertsachen(vhvProdukt),
            einbruchmeldeanlage: mapEinbruchmeldeanlage(vhvProdukt),
            mechanischeSicherung: mapMechanischeSicherungen(vhvProdukt),
            tresore: mapTresore(vhvProdukt),
          }
        : { hasWertsachen: hasWertsachen };
    } else if (
      vhvProdukt.showWertsachensummeQuestion &&
      hoeherwertigerHausratUtils.checkIfShouldShowWersachensummeQuestion(vhvProdukt) &&
      vhvProdukt.hasWertsachen === false
    ) {
      result.hoeherwertigenWertsachen = { hasWertsachen: false };
    }
  }
};

const mapHasWertsachen = (vhvProdukt: VhvCheckoutProdukt): boolean => {
  return hoeherwertigerHausratUtils.checkIfShouldShowWersachensummeQuestion(vhvProdukt)
    ? vhvProdukt.hasWertsachen
    : undefined;
};

const mapGesamtsummeWertsachen = (vhvProdukt: VhvCheckoutProdukt): number => {
  return shouldMapWertsachen(vhvProdukt) ? vhvProdukt.wertsachen?.summe || 0 : undefined;
};

const mapModeaccessoires = (vhvProdukt: VhvCheckoutProdukt): Maccessoires => {
  const modeaccessoires: Modeaccessoires = vhvProdukt.wertsachen?.modeaccessoires;
  return shouldMapWertsachen(vhvProdukt) && modeaccessoires
    ? {
        taschen: modeaccessoires.taschen || undefined,
        schuhpaare: modeaccessoires.schuhpaare || undefined,
        guertel: modeaccessoires.guertel || undefined,
        sonstige: modeaccessoires.sonstige || undefined,
        gesamtsummeModeaccessoires: modeaccessoires.summe || undefined,
      }
    : undefined;
};

const mapVerschlusspflichtigenWertsachen = (
  vhvProdukt: VhvCheckoutProdukt
): VPWertsachen => {
  const verschlusspflichtigeWertsachen: VerschlusspflichtigeWertsachen =
    vhvProdukt.wertsachen?.verschlusspflichtige;
  return shouldMapWertsachen(vhvProdukt) && verschlusspflichtigeWertsachen
    ? {
        bargeld: verschlusspflichtigeWertsachen.bargeld || 0,
        schmucksachen: verschlusspflichtigeWertsachen.schmucksachen || 0,
        schmucksachenEinzelwert:
          vhvProdukt.appId === App.VHV_V2 ? verschlusspflichtigeWertsachen.schmucksachenEinzelwert ||
          undefined : undefined,
        wertpapiere:
          vhvProdukt.appId === App.VHV_V2
            ? undefined
            : verschlusspflichtigeWertsachen.wertpapiere || 0,
        armbandTaschenuhren:
          vhvProdukt.appId === App.VHV_V2
            ? verschlusspflichtigeWertsachen.armbandTaschenuhren ?? undefined
            : undefined,
        armbandTaschenuhrenEinzelwert:
          vhvProdukt.appId === App.VHV_V2
            ? verschlusspflichtigeWertsachen.armbandTaschenuhrenEinzelwert || undefined
            : undefined,
        edelmetalle: verschlusspflichtigeWertsachen.edelmetalle || 0,
        tresoreVorhanden: verschlusspflichtigeWertsachen.tresoreVorhanden,
        gesamtsummeVerschlusspflichtigeWertsachen: verschlusspflichtigeWertsachen.summe || 0,
      }
    : undefined;
};

const shouldActivateForWertsachenfrageDenied = (vhvProdukt: VhvCheckoutProdukt): boolean =>
  vhvProdukt.staendigBewohnt &&
  vhvProdukt.wertsachenValue > 50_000 &&
  vhvProdukt.wertsachenValue <= 100_000 &&
  hoeherwertigerHausratUtils.checkIfShouldShowWersachensummeQuestion(vhvProdukt) &&
  vhvProdukt.hasWertsachen === false;

const shouldMapWertsachen = (vhvProdukt: VhvCheckoutProdukt): boolean => {
  if (vhvProdukt.appId === App.VHV_V2 && vhvProdukt.isTragerisikoSelected) {
    return true;
  }
  return (
    ((vhvProdukt.hasWertsachen ||
        vhvProdukt.versicherungssumme >
        hoeherwertigerHausratUtils.maxVersicherungssummeToShowQuestionFeatureToggleActive) &&
      vhvProdukt.staendigBewohnt) ||
    !hoeherwertigerHausratUtils.checkIfShouldShowWersachensummeQuestion(vhvProdukt)
  );
};

const mapNichtVerschlusspflichtigenWertsachen = (
  vhvProdukt: VhvCheckoutProdukt
): NVPWertsachen => {
  const nichtVerschlusspflichtigeWertsachen: NichtVerschlusspflichtigeWertsachen =
    vhvProdukt.wertsachen?.nichtVerschlusspflichtige;
  return shouldMapWertsachen(vhvProdukt) && nichtVerschlusspflichtigeWertsachen
    ? {
      antiquitaten: nichtVerschlusspflichtigeWertsachen.antiquitaten || 0,
      kunst: nichtVerschlusspflichtigeWertsachen.kunstgegenstaende || 0,
      kunstEinzelwert: vhvProdukt.appId === App.VHV_V2 ? nichtVerschlusspflichtigeWertsachen.kunstgegenstaendeEinzelwert || undefined : undefined,
      silber: nichtVerschlusspflichtigeWertsachen.silbersachen || 0,
      emaVorhanden: nichtVerschlusspflichtigeWertsachen.einbruchmeldeanlageVorhanden,
      gesamtsummeNichtverschlusspflichtigeWertsachen:
        nichtVerschlusspflichtigeWertsachen.summe || 0
    }
    : undefined;
};

const mapTresore = (vhvProdukt: VhvCheckoutProdukt): Tresor[] => {
  return hoeherwertigerHausratUtils.showWertbehaeltnisseKachel(vhvProdukt) &&
  vhvProdukt.tresor?.status == 'COMPLETE'
    ? vhvProdukt.tresor?.eintraege
      ?.filter(tresor => !isEqual(tresor, INITIAL_TRESOR_EINTRAG))
      .map((tresor, index) => {
        return {
          tresorId: index + 1,
          variante: mapTresorsicherheitsstuffeToVariante(tresor),
          gewicht: tresor.gewicht,
          verankerung: mapBooleanToHoeherwertigerSelection(tresor.verankert),
          ema: mapBooleanToHoeherwertigerSelection(tresor.ueberwachung),
          verbaut: mapVerbauung(tresor.verbauung)
        };
      })
    : undefined;
};

const mapVerbauung = (verbauung: TresorVerbauung): Verbauung => {
  return {
    [TresorVerbauung.EINGEMAUERT]: Verbauung.EINGEMAUERT,
    [TresorVerbauung.FREISTEHEND]: Verbauung.FREISTEHEND
  }[verbauung];
};

const mapTresorsicherheitsstuffeToVariante = (tresor: TresorEintrag): number => {
  return {
    [TresorSicherheitsstufe.OHNE]: 1,
    [TresorSicherheitsstufe.B]: 2,
    [TresorSicherheitsstufe.C1F]: 3,
    [TresorSicherheitsstufe.C2F]: 4,
    [TresorSicherheitsstufe.D1]: 5,
    [TresorSicherheitsstufe.D2]: 6
  }[tresor.sicherheitsstufe];
};

const mapBooleanToHoeherwertigerSelection = (value?: boolean): HoeherwertigerSelection => {
  if (value != null) {
    return value ? HoeherwertigerSelection.JA : HoeherwertigerSelection.NEIN;
  }
  return undefined;
};

const mapEinbruchmeldeanlage = (vhvProdukt: VhvCheckoutProdukt): Einbruchmeldeanlage => {
  const hasEma = hoeherwertigerHausratUtils.showEinbruchmeldeanlageKachel(vhvProdukt);
  return hasEma &&
  vhvProdukt.einbruchMeldeanlage &&
  !isEqual(vhvProdukt.einbruchMeldeanlage, INITIAL_EMA) &&
  vhvProdukt.einbruchMeldeanlage.status === 'COMPLETE'
    ? {
      klasse: vhvProdukt.einbruchMeldeanlage?.klasse,
      klasseDetail: vhvProdukt.einbruchMeldeanlage?.meldeanlageBeschreibung,
      ema2170: mapBooleanToHoeherwertigerSelection(vhvProdukt.einbruchMeldeanlage?.vds2170),
      ema2529: mapBooleanToHoeherwertigerSelection(vhvProdukt.einbruchMeldeanlage?.vds2529)
    }
    : undefined;
};

const mapKeller = (vhvProdukt: VhvCheckoutProdukt): Keller => {
  return vhvProdukt.mechanischeSicherungen?.kellerfenster &&
  !isEqual(
    vhvProdukt.mechanischeSicherungen?.aussentueren,
    INITIAL_MECHANISCHE_SICHERUNGEN.kellerfenster
  )
    ? {
      kellerblende: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.kellerfenster.stahllochblende?.vorhanden
      ),
      kellerblendeDatum:
      vhvProdukt.mechanischeSicherungen.kellerfenster.stahllochblende?.nachruestungDatum,
      kellergitter: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.kellerfenster.gitter?.vorhanden
      ),
      kellergitterDatum:
      vhvProdukt.mechanischeSicherungen.kellerfenster.gitter?.nachruestungDatum,
      kellerverankerung: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.kellerfenster.lichtschachtrost?.vorhanden
      ),
      kellerverankerungDatum:
      vhvProdukt.mechanischeSicherungen.kellerfenster.stahllochblende?.nachruestungDatum
    }
    : undefined;
};

const mapTuer = (vhvProdukt: VhvCheckoutProdukt): Tuer => {
  return vhvProdukt.mechanischeSicherungen?.aussentueren &&
  !isEqual(
    vhvProdukt.mechanischeSicherungen?.aussentueren,
    INITIAL_MECHANISCHE_SICHERUNGEN.aussentueren
  )
    ? {
      schliesszylinder: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.aussentueren.schliesszylinder?.vorhanden
      ),
      zylinderDatum:
      vhvProdukt.mechanischeSicherungen.aussentueren.schliesszylinder?.nachruestungDatum,
      querriegel: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.aussentueren.querriegelschloss?.vorhanden
      ),
      querriegelDatum:
      vhvProdukt.mechanischeSicherungen.aussentueren.querriegelschloss?.nachruestungDatum,
      verglasung: mapBooleanToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.aussentueren.verglasungen?.vorhanden
      ),
      tuergiter: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.aussentueren.verglasungen.gitter?.vorhanden
      ),
      tuergiterDatum:
      vhvProdukt.mechanischeSicherungen.aussentueren.verglasungen.gitter?.nachruestungDatum,
      vdsVerglasung: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.aussentueren.verglasungen.eh1Verglasung?.vorhanden
      ),
      vdsVerglasungDatum:
      vhvProdukt.mechanischeSicherungen.aussentueren.verglasungen.eh1Verglasung
        ?.nachruestungDatum,
      weitereSicherung: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.aussentueren.verglasungen.andereSicherung?.vorhanden
      ),
      weitereSicherungDetailsDatum:
      vhvProdukt.mechanischeSicherungen.aussentueren.verglasungen.andereSicherung
        ?.nachruestungDatum,
      weitereSicherungDetails:
      vhvProdukt.mechanischeSicherungen.aussentueren.verglasungen.andereSicherung
        ?.nachruestungBeschreibung
    }
    : undefined;
};

const mapMechanischeSicherungen = (vhvProdukt: VhvCheckoutProdukt): MechanischeSicherung => {
  const showMechansicheSicherungKachel =
    hoeherwertigerHausratUtils.showMechansicheSicherungKachel(vhvProdukt);
  return showMechansicheSicherungKachel &&
  !isEqual(vhvProdukt.mechanischeSicherungen, INITIAL_MECHANISCHE_SICHERUNGEN) &&
  vhvProdukt.mechanischeSicherungen?.status === 'COMPLETE'
    ? {
      terrasseBalkon: mapTerrasseBalkon(vhvProdukt),
      tuer: mapTuer(vhvProdukt),
      keller: mapKeller(vhvProdukt),
      zusatzsicherung: vhvProdukt.mechanischeSicherungen?.weitereAbsicherungen
    }
    : undefined;
};

const mapSicherungVorhandenToHoeherwertigerSelection = (
  sicherungVorhanden: SicherungVorhanden | SicherungVorhandenMitRelevanz
): HoeherwertigerSelection => {
  switch (sicherungVorhanden) {
    case SicherungVorhanden.JA:
    case SicherungVorhandenMitRelevanz.JA:
      return HoeherwertigerSelection.JA;
    case SicherungVorhanden.NEIN:
    case SicherungVorhandenMitRelevanz.NEIN:
      return HoeherwertigerSelection.NEIN;
    case SicherungVorhanden.WIRD_NACHGERUESTET:
    case SicherungVorhandenMitRelevanz.WIRD_NACHGERUESTET:
      return HoeherwertigerSelection.WIRD_NACHGERUSTET;
    case SicherungVorhandenMitRelevanz.NICHT_RELEVANT:
      return HoeherwertigerSelection.NICHT_RELEVANT;
    default:
      return undefined;
  }
};

const mapTerrasseBalkon = (vhvProdukt: VhvCheckoutProdukt): TerrasseBalkon => {
  let result: TerrasseBalkon;
  if (
    vhvProdukt.mechanischeSicherungen?.balkontueren &&
    !isEqual(
      vhvProdukt.mechanischeSicherungen?.balkontueren,
      INITIAL_MECHANISCHE_SICHERUNGEN.balkontueren
    )
  ) {
    result = {
      balkonschloss: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.balkontueren.zusatzschloss?.vorhanden
      ),
      balkonschlossDatum:
      vhvProdukt.mechanischeSicherungen.balkontueren.zusatzschloss?.nachruestungDatum,
      zweiFachVerrieglung: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.balkontueren.zweifachverriegelung?.vorhanden
      ),
      zweiFachVerrieglungDatum:
      vhvProdukt.mechanischeSicherungen.balkontueren.zweifachverriegelung?.nachruestungDatum,
      weitereBalkonSicherung: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.balkontueren.andereSicherung?.vorhanden
      ),
      weitereBalkonSicherungDetailsDatum:
      vhvProdukt.mechanischeSicherungen.balkontueren.andereSicherung?.nachruestungDatum,
      weitereBalkonSicherungDetails:
      vhvProdukt.mechanischeSicherungen.balkontueren.andereSicherung?.nachruestungBeschreibung
    };
  }
  if (
    vhvProdukt.mechanischeSicherungen?.fenster &&
    !isEqual(vhvProdukt.mechanischeSicherungen?.fenster, INITIAL_MECHANISCHE_SICHERUNGEN.fenster)
  ) {
    result = {
      ...result,
      fensterSchloss: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.fenster.zusatzschloss?.vorhanden
      ),
      fensterSchlossDatum:
      vhvProdukt.mechanischeSicherungen.fenster.zusatzschloss?.nachruestungDatum,
      fensterZweifachSchloss: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.fenster.zweifachverriegelung?.vorhanden
      ),
      fensterZweifachSchlossDatum:
      vhvProdukt.mechanischeSicherungen.fenster.zweifachverriegelung?.nachruestungDatum,
      andereFenster: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.fenster.andereSicherung?.vorhanden
      ),
      andereFensterDetailsDatum:
      vhvProdukt.mechanischeSicherungen.fenster.andereSicherung?.nachruestungDatum,
      andereFensterDetails:
      vhvProdukt.mechanischeSicherungen.fenster.andereSicherung?.nachruestungBeschreibung,
      fenstergitter: mapSicherungVorhandenToHoeherwertigerSelection(
        vhvProdukt.mechanischeSicherungen.fenster.gitter?.vorhanden
      ),
      fenstergitterDatum: vhvProdukt.mechanischeSicherungen.fenster.gitter?.nachruestungDatum
    };
  }
  return result;
};

const getPreviousContractExclusions = (tkvAngaben: TkvAngabenV2Item): string[] | undefined => {
  const entries = tkvAngaben.previousHealthStatusEntries;
  if (!entries?.length) {
    return undefined;
  }

  const exclusions = entries
    .flatMap(entry => {
      if (!entry.notInsuredText?.trim()) {
        return [];
      }

      if (entry.hasDescription && entry.diagnosis === 'Sonstiges/nicht aufgelistet' && entry.description) {
        return [`Sonstiges: ${entry.description.trim()}`];
      }

      if (entry.hasDescription && entry.description) {
        return [entry.description.trim()];
      }

      const baseText = entry.notInsuredText.trim();

      if (entry.selectedDetails?.length > 0) {
        return entry.selectedDetails
          .filter(detail => detail?.trim())
          .map(detail => `${baseText}, ${detail.trim()}`);
      }

      return [baseText];
    })
    .filter(isNonEmptyString);

  return exclusions.length > 0 ? exclusions : undefined;
};

const isSpecialNoImpactDiagnosis = (diagnosis: string): boolean => {
  return (
    diagnosis === 'Taubheit' ||
    diagnosis === 'Fehlende Gliedmaße' ||
    diagnosis === 'Fehlendes Auge' ||
    diagnosis === 'Fehlendes Ohr'
  );
};

const getExclusions = (tkvAngaben: TkvAngabenV2Item): string[] | undefined => {
  const selectedIllnessAreas = tkvAngaben.selectedIllnesses?.map(illness => illness.name);

  const newExclusions = (tkvAngaben.healthStatusEntries || [])
    .filter(entry =>
      !selectedIllnessAreas ||
      selectedIllnessAreas.includes(entry.area) ||
      (entry.isNotInsuredIfHealed && !entry.notInsuredText && isSpecialNoImpactDiagnosis(entry.diagnosis))
    )
    .flatMap(entry => buildExclusionTexts(entry))
    .filter(isNonEmptyString);

  return newExclusions.length > 0 ? newExclusions : undefined;
};

const buildExclusionTexts = (entry: HealthStatusEntry): string[] => {
  const treatmentStatus = entry.treatmentStatus?.length
    ? entry.treatmentStatus
    : [{ isHealed: null }];

  const isSpecialCase = entry.isNotInsuredIfHealed && !entry.notInsuredText && isSpecialNoImpactDiagnosis(entry.diagnosis);

  return treatmentStatus
    .filter(status =>
      (entry.notInsuredText && (!status.isHealed || entry.isNotInsuredIfHealed)) ||
      (isSpecialCase && status.isHealed)
    )
    .map(status => {
      if (entry.hasDescription && entry.diagnosis === 'Sonstiges/nicht aufgelistet' && entry.description) {
        return `Sonstiges: ${entry.description.trim()}`;
      }

      if (entry.hasDescription && entry.description) {
        return entry.description.trim();
      }

      const baseText = isSpecialCase
        ? entry.diagnosis?.trim() || ''
        : entry.notInsuredText?.trim() || '';

      if (isSpecialCase) {
        return baseText;
      }

      const detail = status.selectedDetail?.trim();
      if (detail) {
        return `${baseText}, ${detail}`;
      }
      return baseText;
    })
    .filter(text => text.length > 0);
};

const isNonEmptyString = (value: string | null): value is string => {
  return !isNullOrUndefined(value) && value.trim().length > 0;
};

const getSelectedDetailsForEntry = (entry: HealthStatusEntry, selectedDetail?: string): string[] => {
  if (entry.hasDescription && entry.description?.trim()) {
    return [entry.description.trim()];
  }
  if (selectedDetail) {
    return [selectedDetail];
  }
  return entry.selectedDetails || [];
};

const getTkvDiagnoseDruckList = (tkvAngaben: TkvAngabenV2Item): TkvV2HealthStatus[] => {
  const entries = tkvAngaben.healthStatusEntries;
  if (!entries?.length) {
    return [];
  }

  return entries
    .flatMap(entry => {
      const treatmentStatus = entry.treatmentStatus?.length
        ? entry.treatmentStatus
        : [{ isHealed: null }];

      const hasIndividualStatuses = treatmentStatus.some(status => status.selectedDetail);

      if (hasIndividualStatuses) {
        return treatmentStatus.map(status => ({
          area: entry.area,
          diagnosis: entry.diagnosis,
          status: mapIndividualStatus(status.isHealed, entry.notInsuredText, entry.isNotInsuredIfHealed, entry.diagnosis),
          selectedDetails: getSelectedDetailsForEntry(entry, status.selectedDetail)
        } as TkvV2HealthStatus));
      }

      return [{
        area: entry.area,
        diagnosis: entry.diagnosis,
        status: mapOverallStatus(treatmentStatus, entry.notInsuredText, entry.isNotInsuredIfHealed, entry.diagnosis),
        selectedDetails: getSelectedDetailsForEntry(entry)
      } as TkvV2HealthStatus];
    })
    .filter(status => status.diagnosis?.trim()) as TkvV2HealthStatus[];
};

const mapIndividualStatus = (
  isHealed: boolean | null,
  notInsuredText?: string,
  isNotInsuredIfHealed?: boolean,
  diagnosis?: string
): string => {
  if (diagnosis && isSpecialNoImpactDiagnosis(diagnosis)) {
    return '';
  }
  if (!notInsuredText) {
    return 'ausgeheilt';
  }
  if (diagnosis === 'Sonstiges/nicht aufgelistet' && isHealed === true) {
    return 'ausgeheilt';
  }
  if (isNotInsuredIfHealed) {
    return 'nicht ausgeheilt';
  }
  return isHealed ? 'ausgeheilt' : 'nicht ausgeheilt';
};

const mapOverallStatus = (
  treatmentStatus: Array<{
    selectedDetail?: string;
    isHealed: boolean;
  }> | undefined,
  notInsuredText?: string,
  isNotInsuredIfHealed?: boolean,
  diagnosis?: string
): string => {
  if (diagnosis && isSpecialNoImpactDiagnosis(diagnosis)) {

    return '';
  }
  if (!notInsuredText) {
    return 'ausgeheilt';
  }

  if (diagnosis === 'Sonstiges/nicht aufgelistet' && treatmentStatus?.length) {
    const allHealed = treatmentStatus.every(status => status.isHealed === true);
    if (allHealed) {
      return 'ausgeheilt';
    }
  }

  if (isNotInsuredIfHealed) {
    return 'nicht ausgeheilt';
  }

  if (!treatmentStatus?.length) {
    return 'nicht ausgeheilt';
  }

  const allHealed = treatmentStatus.every(status => status.isHealed === true);
  return allHealed ? 'ausgeheilt' : 'nicht ausgeheilt';
};

const applyTkvV2Druckdaten = (
  tkvDruckdaten: TkvV2Druckdaten,
  state: IAppState,
  angebotsId: string
): void => {
  const tkvAngaben: TkvAngabenV2Item = state.tkvAngabenV2.angabenItems.find(
    elem => elem.angebotsId === angebotsId
  );
  tkvDruckdaten.versichertesTier.tiername = tkvAngaben.animalName;
  tkvDruckdaten.versichertesTier.istBeiTierarzt = false;
  tkvDruckdaten.versichertesTier.einsatzgebiet = tkvAngaben.isAssistanceDog
    ? EinsatzgebieteTier.ASSISTENZHUND
    : null;
  tkvDruckdaten.versichertesTier.kennzeichnung = tkvAngaben.animalIdentification;
  tkvDruckdaten.versichertesTier.kennzeichnungsArt = tkvAngaben.animalIdentificationType;
  tkvDruckdaten.versichertesTier.exclusions = getExclusions(tkvAngaben);
  tkvDruckdaten.versichertesTier.tkvDiagnoseDruckList = getTkvDiagnoseDruckList(tkvAngaben);
  tkvDruckdaten.versichertesTier.previousContractExclusions = getPreviousContractExclusions(tkvAngaben);
};
